import Button from '@orn/shared-web/components/Button/Button';
import { RadioButtonCard, RadioGroup } from '@orn/shared-web/components/RadioButton/RadioButtons';

import { SubscriptionInterval } from '../../../__generated__/globalTypes';
import { cadenceLabel, type IBillingCadence } from '../../../lib/billing/billingCadence';

export interface IPlanOption {
  interval: SubscriptionInterval;
  /**
   * What this option's price debits on. The label comes from here rather than
   * from `interval`, which names only the catalog slot the price sits in.
   */
  cadence: IBillingCadence;
  /** Server-formatted, e.g. "$99.00/year" — rendered verbatim. */
  amountDisplay: string;
  trialDays: number;
}

/** Annual first — it is the pre-selected, discounted option. */
const INTERVAL_ORDER: readonly SubscriptionInterval[] = [SubscriptionInterval.Annual, SubscriptionInterval.Monthly];

/**
 * Screen 1 of checkout — monthly or annual.
 *
 * One decision per screen: this picks what is being bought, and the account it
 * debits comes after. Amounts are rendered exactly as the server formatted
 * them, because the same helpers write the figure onto the mandate evidence
 * row.
 */
export interface IPlanPickerBaseProps {
  /** One entry per offered interval. Annual is presented first. */
  plans: readonly IPlanOption[];
  selectedInterval: SubscriptionInterval;
  onSelectInterval: (interval: SubscriptionInterval) => void;
  onContinue: () => void;
  /** Leaves checkout without subscribing; rendered only when the host provides it. */
  onCancel?: () => void;
}

const PlanPickerBase = ({ plans, selectedInterval, onSelectInterval, onContinue, onCancel }: IPlanPickerBaseProps) => {
  const ordered = INTERVAL_ORDER.map((interval) => plans.find((plan) => plan.interval === interval)).filter(
    (plan): plan is IPlanOption => plan !== undefined
  );
  const trialDays = ordered[0]?.trialDays;

  return (
    <div className="flex w-full flex-col gap-6">
      {trialDays !== undefined && (
        <div className="bg-utility-brand-50 flex w-full flex-col items-center gap-1 rounded-xl px-4 py-3">
          <p className="text-primary text-md font-medium">Free for {trialDays} days</p>
          <p className="text-tertiary text-sm">$0 today.</p>
        </div>
      )}

      <RadioGroup
        aria-label="Billing interval"
        value={selectedInterval}
        onChange={(value) => onSelectInterval(value as SubscriptionInterval)}
        className="w-full"
      >
        {ordered.map((plan) => (
          <RadioButtonCard
            key={plan.interval}
            value={plan.interval}
            label={cadenceLabel(plan.cadence)}
            hint={plan.amountDisplay}
          />
        ))}
      </RadioGroup>

      <Button type="button" size="xl" color="primary" className="w-full" onClick={onContinue}>
        Continue
      </Button>
      {onCancel !== undefined && (
        <Button type="button" size="md" color="link-gray" className="w-full" onClick={onCancel}>
          Cancel
        </Button>
      )}
    </div>
  );
};

PlanPickerBase.displayName = 'PlanPickerBase';

export default PlanPickerBase;
